import { Minus, Plus, RotateCcw } from "lucide-react";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { BatchRecord, LocationNode, ProductAnalysis } from "../types";
import {
  racksFor,
  stockStateLabel,
  warehouseZone,
  WAREHOUSE_LOCATION_COUNT,
  WAREHOUSE_SPECIAL_LOCATIONS,
  WAREHOUSE_SPECIAL_ZONE,
  WAREHOUSE_ZONE_COUNT,
  type MainAisleLayout,
  type SmallAisleLayout,
  type WarehouseMapLayout,
  type WarehouseZoneSpec,
} from "../lib/warehouse";

export type WarehouseLevel = "zones" | "overview" | "main" | "small" | "rack" | "full";
export type LocationFilter = "all" | "stocked" | "empty" | "unknown";

export interface WarehouseSelection {
  level: WarehouseLevel;
  mainAisle: string | null;
  smallAisle: string | null;
  selectedCode: string | null;
}

interface WarehouseMap2DProps {
  layout: WarehouseMapLayout;
  zones: readonly WarehouseZoneSpec[];
  selection: WarehouseSelection;
  filter: LocationFilter;
  products: Map<string, ProductAnalysis[]>;
  productNamesByLocation: ReadonlyMap<string, readonly string[]>;
  batchesByLocation: Map<string, BatchRecord[]>;
  dataStatus?: string;
  recommendedLocations?: ReadonlySet<string>;
  onOpenZone: (zone: string) => void;
  onOpenMain: (mainAisle: string) => void;
  onOpenSmall: (mainAisle: string, smallAisle: string) => void;
  onOpenRack: (node: LocationNode) => void;
  onBack: () => void;
}

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const OVERVIEW_VIEW: ViewBox = { x: 18, y: 22, width: 1664, height: 1060 };
const ZONES_VIEW: ViewBox = { x: 0, y: 0, width: 1700, height: 1100 };
const ZONES_VIEW_BOX = "0 0 1700 1100";
const FULL_VIEW: ViewBox = { x: 18, y: 22, width: 1664, height: 1060 };
const SMALL_VIEW: ViewBox = { x: 0, y: 0, width: 1100, height: 620 };
const RACK_VIEW: ViewBox = { x: 0, y: 0, width: 1100, height: 620 };
const ANIMATION_MS = 240;
const ZOOM_ANIMATION_MS = 110;
const EMPTY_RECOMMENDED = new Set<string>();

function easeOutCubic(value: number) {
  return 1 - (1 - value) ** 3;
}

function roundedCoordinate(value: number) {
  return Math.round(value * 100) / 100;
}

function viewBoxAttribute(box: ViewBox) {
  return `${roundedCoordinate(box.x)} ${roundedCoordinate(box.y)} ${roundedCoordinate(box.width)} ${roundedCoordinate(box.height)}`;
}

function normalizedRect(rect: DOMRect): ViewportRect {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function mainView(main?: MainAisleLayout): ViewBox {
  if (!main) return { x: 80, y: 42, width: 400, height: 1040 };
  return { x: main.x - 205, y: 42, width: 410, height: 1040 };
}

function activateWithKeyboard(
  event: ReactKeyboardEvent<SVGGElement>,
  action: () => void,
) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  action();
}

function filterMatches(node: LocationNode, filter: LocationFilter) {
  return filter === "all" || node.stockState === filter;
}

function stateClass(node: LocationNode) {
  return `stock-${node.stockState}`;
}

function stockClass(state: LocationNode["stockState"]) {
  return `stock-${state}`;
}

function productLabel(names?: readonly string[]) {
  if (!names?.length) return "";
  const characters = Array.from(names[0].trim());
  if (!characters.length) return "";
  const first = characters.length > 9
    ? `${characters.slice(0, 9).join("")}…`
    : names[0].trim();
  return names.length > 1 ? `${first} 等 ${names.length} 品` : first;
}

function productTitle(names?: readonly string[]) {
  if (!names?.length) return "";
  return [...new Set(names.map((name) => name.trim()).filter(Boolean))].join(" · ");
}

const WarehousePerimeter = memo(function WarehousePerimeter({
  layout,
  detailed = false,
}: {
  layout: WarehouseMapLayout;
  detailed?: boolean;
}) {
  return (
    <g className="warehouse-perimeter" aria-hidden="true">
      <rect x={34} y={46} width={1632} height={1012} rx={6} className="warehouse-slab" />
      <rect x={48} y={60} width={1604} height={984} rx={3} className="warehouse-slab-inner" />
      <rect x={64} y={76} width={1572} height={952} rx={2} className="warehouse-safety-floor" />
      <rect x={76} y={88} width={1548} height={928} className="warehouse-inner-wall" />
      <path d="M86 1032 H1616" className="warehouse-floor-edge" />
      <path d="M86 80 V1018 M1614 80 V1018" className="warehouse-floor-edge" />
      <rect x={44} y={866} width={170} height={178} rx={2} className="warehouse-dock-zone" />
      <path d="M54 1017 H202 M54 1032 H202" className="warehouse-dock-stripes" />
      <rect x={68} y={894} width={122} height={78} rx={1} className="warehouse-dock-gate" />
      <path d="M81 906 V959 M101 906 V959 M121 906 V959 M141 906 V959 M161 906 V959" className="warehouse-dock-ribs" />
      <rect x={86} y={1000} width={430} height={36} rx={1} className="warehouse-dispatch-lane" />
      <path d="M94 1006 H516 M94 1030 H516" className="warehouse-traffic-edge" />
      <text x={104} y={1023} className="warehouse-dock-label">
        出货缓冲区 / OUTBOUND
      </text>
      <g className="warehouse-zone-mark">
        <text x={layout.zone.length === 1 ? 1370 : 1330} y={122}>{layout.zone}</text>
        <text x={layout.zone.length === 1 ? 1430 : 1430} y={121}>标准库区 · 智能仓储平面</text>
        <text x={1430} y={140}>WAREHOUSE ZONE / 2D LIVE MAP</text>
      </g>
      {detailed && (
        <g className="warehouse-concrete-joints">
          {[250, 520, 790, 1060, 1330].map((x) => (
            <path key={`v-${x}`} d={`M${x} 82 V1016`} />
          ))}
          {[280, 510, 740, 970].map((y) => (
            <path key={`h-${y}`} d={`M72 ${y} H1628`} />
          ))}
        </g>
      )}
      <g className="warehouse-structural-columns">
        {[
          [112, 110], [450, 110], [790, 110], [1130, 110], [1548, 110],
          [112, 478], [450, 478], [790, 478], [1130, 478], [1548, 478],
          [332, 838], [670, 838], [1010, 838], [1350, 838],
        ].map(([x, y]) => (
          <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
            <rect x={-7} y={-7} width={14} height={14} rx={2} />
            <circle r={2.2} />
          </g>
        ))}
      </g>
      <g className="warehouse-scale" transform="translate(1390 1018)">
        <path d="M0 0 H128 M0 -5 V5 M64 -3 V3 M128 -5 V5" />
        <text x={64} y={18} textAnchor="middle">10 m</text>
      </g>
      <g className="warehouse-north-mark" transform="translate(1572 1040)">
        <path d="M0 8 V-14 M-6 -5 L0 -14 L6 -5" />
        <text y={24} textAnchor="middle">N</text>
      </g>
      <text x={82} y={880} className="warehouse-route-note">路线基准：货架拣货边 → 小通道 → 主通道 → 出货口</text>
      <title>{`${layout.zone} 库区，共 ${layout.stats.racks} 个货架`}</title>
    </g>
  );
});

const MainLane = memo(function MainLane({
  aisle,
  interactive,
  label,
  onActivate,
}: {
  aisle: MainAisleLayout;
  interactive: boolean;
  label: boolean;
  onActivate?: () => void;
}) {
  return (
    <g
      className={`warehouse-main-lane ${interactive ? "is-interactive" : ""}`}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `打开 ${aisle.number} 号主通道，共 ${aisle.smallAisles} 条小通道` : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={interactive && onActivate
        ? (event) => activateWithKeyboard(event, onActivate)
        : undefined}
    >
      {interactive && (
        <rect
          x={aisle.x - 52}
          y={aisle.top - 22}
          width={104}
          height={aisle.height + 44}
          rx={4}
          className="warehouse-main-hit"
        />
      )}
      <rect
        x={aisle.x - aisle.width / 2 - 14}
        y={aisle.top - 16}
        width={aisle.width + 28}
        height={aisle.height + 32}
        rx={3}
        className="warehouse-main-shoulder"
      />
      <rect
        x={aisle.x - aisle.width / 2}
        y={aisle.top}
        width={aisle.width}
        height={aisle.height}
        rx={1.5}
        className="warehouse-main-lane-fill"
      />
      <path
        d={`M${aisle.x - aisle.width / 2 + 4} ${aisle.top + 4} V${aisle.bottom - 4} M${aisle.x + aisle.width / 2 - 4} ${aisle.top + 4} V${aisle.bottom - 4}`}
        className="warehouse-main-edge"
      />
      <path
        d={`M${aisle.x} ${aisle.top + 20} V${aisle.bottom - 20}`}
        className="warehouse-main-direction"
      />
      {[0.16, 0.42, 0.68, 0.9].map((ratio) => {
        const arrowY = aisle.top + aisle.height * ratio;
        return (
          <g key={ratio} className="warehouse-main-arrow">
            <path d={`M${aisle.x - 6} ${arrowY + 9} L${aisle.x} ${arrowY + 1} L${aisle.x + 6} ${arrowY + 9}`} />
          </g>
        );
      })}
      {label && (
        <>
          <g transform={`translate(${aisle.x} ${aisle.top - 46})`} className="warehouse-main-number">
            <rect x={-22} y={-15} width={44} height={30} rx={2} />
            <text y={5} textAnchor="middle">{aisle.number}</text>
          </g>
          <text x={aisle.x} y={aisle.bottom + 45} textAnchor="middle" className="warehouse-main-label">
            {aisle.code}
          </text>
          <text x={aisle.x} y={aisle.bottom + 62} textAnchor="middle" className="warehouse-main-meta">
            {aisle.smallAisles} 条小通道
          </text>
        </>
      )}
      <title>{`${aisle.code} 主通道 · ${aisle.smallAisles} 条小通道`}</title>
    </g>
  );
});

const MiniBranch = memo(function MiniBranch({
  branch,
  filter,
  active,
  recommended,
  interactive,
  detailed,
  onActivate,
}: {
  branch: SmallAisleLayout;
  filter: LocationFilter;
  active?: boolean;
  recommended?: boolean;
  interactive: boolean;
  detailed?: boolean;
  onActivate?: () => void;
}) {
  const matched = filter === "all" || branch.stockState === filter;
  const labelX = branch.side === "left" ? branch.branchEnd.x - 12 : branch.branchEnd.x + 12;
  const branchX = Math.min(branch.junction.x, branch.branchEnd.x);
  const branchWidth = Math.abs(branch.branchEnd.x - branch.junction.x);
  return (
    <g
      className={[
        "warehouse-small-branch",
        branch.side === "left" ? "is-left" : "is-right",
        active ? "is-active" : "",
        recommended ? "has-recommendation" : "",
        matched ? "" : "is-dim",
        interactive ? "is-interactive" : "",
      ].join(" ")}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive
        ? `打开 ${branch.code} 小通道，最近货架距出货口 ${branch.distanceToGate.toFixed(1)} 米`
        : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={interactive && onActivate
        ? (event) => activateWithKeyboard(event, onActivate)
        : undefined}
    >
      {interactive && (
        <rect
          x={branchX - 7}
          y={branch.branchY - 18}
          width={branchWidth + 14}
          height={36}
          rx={2}
          className="warehouse-branch-hit"
        />
      )}
      <rect
        x={branchX}
        y={branch.branchY - 12}
        width={branchWidth}
        height={24}
        rx={1}
        className="warehouse-branch-fill"
      />
      <path
        d={`M${branchX + 5} ${branch.branchY} H${branchX + branchWidth - 5}`}
        className="warehouse-branch-centre"
      />
      <path
        d={`M${branchX + 8} ${branch.branchY - 9} H${branchX + branchWidth - 8} M${branchX + 8} ${branch.branchY + 9} H${branchX + branchWidth - 8}`}
        className="warehouse-branch-edge"
      />
      <circle cx={branch.junction.x} cy={branch.branchY} r={2.5} className="warehouse-branch-junction" />
      {recommended && (
        <circle
          cx={branch.side === "left" ? branch.branchEnd.x - 3 : branch.branchEnd.x + 3}
          cy={branch.branchY}
          r={4}
          className="warehouse-branch-recommendation"
        />
      )}
      {(detailed || interactive) && (
        <>
          <rect
            x={labelX - (branch.side === "left" ? 35 : 0)}
            y={branch.branchY - 17}
            width={35}
            height={11}
            rx={1.5}
            className={`warehouse-branch-chip ${stockClass(branch.stockState)}`}
          />
          <text
            x={labelX - (branch.side === "left" ? 17.5 : -17.5)}
            y={branch.branchY - 8.4}
            textAnchor="middle"
            className="warehouse-branch-number"
          >
            {branch.number}
          </text>
        </>
      )}
      <title>{`${branch.code} · 最近货架 ${branch.distanceToGate.toFixed(1)} m · ${stockStateLabel(branch.stockState)}`}</title>
    </g>
  );
});

const OverviewScene = memo(function OverviewScene({
  layout,
  runAction,
  onOpenMain,
}: {
  layout: WarehouseMapLayout;
  runAction: (action: () => void) => void;
  onOpenMain: (mainAisle: string) => void;
}) {
  return (
    <g className="scene-overview">
      <WarehousePerimeter layout={layout} detailed />
      <path d="M112 1000 V941 H1490" className="warehouse-dispatch-path" />
      <g transform="translate(112 1000)" className="warehouse-exit-node">
        <circle r={27} className="warehouse-exit-halo" />
        <circle r={11} className="warehouse-exit-core" />
        <text y={44} textAnchor="middle">出货口</text>
        <text y={59} textAnchor="middle">OUTBOUND</text>
      </g>
      <g className="warehouse-overview-title">
        <text x={255} y={128}>{layout.zone} · 智能仓储库区</text>
        <text x={255} y={149}>点击浅蓝色主通道进入仓内 · 全图模式可查看 {layout.stats.racks} 个货架</text>
      </g>
      {layout.mainAisles.map((aisle) => (
        <MainLane
          key={aisle.code}
          aisle={aisle}
          interactive
          label
          onActivate={() => runAction(() => onOpenMain(aisle.number))}
        />
      ))}
      <g className="warehouse-route-axis">
        <path d="M150 940 H1500" />
        <path d="M1474 933 L1490 940 L1474 947" />
        <text x={1290} y={928}>分发缓冲区 / DISPATCH CORRIDOR</text>
      </g>
      <g className="warehouse-nearest-note" transform="translate(278 878)">
        <path d="M-12 0 H-4 M0 -6 V6" />
        <text x={8} y={4}>01 小通道靠近出货口</text>
      </g>
    </g>
  );
});

const ZONE_GRID_GEOMETRY = {
  x: 344,
  y: 176,
  width: 300,
  height: 112,
  columnStep: 322,
  rowStep: 139,
} as const;

function ZoneCard({
  zone,
  active,
  runAction,
  onOpenZone,
}: {
  zone: WarehouseZoneSpec;
  active: boolean;
  runAction: (action: () => void) => void;
  onOpenZone: (zone: string) => void;
}) {
  const x = ZONE_GRID_GEOMETRY.x + (zone.column - 1) * ZONE_GRID_GEOMETRY.columnStep;
  const y = ZONE_GRID_GEOMETRY.y + (zone.row - 1) * ZONE_GRID_GEOMETRY.rowStep;
  const main = zone.kind === "main";
  return (
    <g
      transform={`translate(${x} ${y})`}
      className={[
        "warehouse-zone-card",
        main ? "is-main-zone" : "is-aux-zone",
        zone.nearestOutbound ? "is-nearest-outbound" : "",
        active ? "is-current" : "",
      ].join(" ")}
      role="button"
      tabIndex={0}
      data-zone-code={zone.code}
      aria-label={`${zone.code} ${main ? "主库区" : "辅库区"}，点击查看内部主通道`}
      onClick={() => runAction(() => onOpenZone(zone.code))}
      onKeyDown={(event) => activateWithKeyboard(event, () => onOpenZone(zone.code))}
    >
      <rect width={ZONE_GRID_GEOMETRY.width} height={ZONE_GRID_GEOMETRY.height} rx={2} className="warehouse-zone-card-shell" />
      <rect x={5} y={5} width={290} height={102} className="warehouse-zone-cell-inset" />
      <rect width={4} height={ZONE_GRID_GEOMETRY.height} className="warehouse-zone-card-accent" />
      <path d="M16 31 H284" className="warehouse-zone-card-rule" />
      <g transform="translate(20 21)">
        <text className="warehouse-zone-card-code">{zone.code}</text>
        <text x={zone.code.length === 1 ? 32 : 54} y={0} className="warehouse-zone-card-kind">
          {main ? "主库区" : "辅库区"}
        </text>
        {active && (
          <g transform="translate(194 -5)">
            <rect width={34} height={13} rx={1.5} className="warehouse-zone-current-badge" />
            <circle cx={7} cy={6.5} r={2.2} className="warehouse-zone-current-dot" />
            <text x={13} y={9}>当前</text>
          </g>
        )}
      </g>
      <g transform="translate(16 39)" className="warehouse-zone-miniature" aria-hidden="true">
        <rect width={268} height={62} className="warehouse-zone-plan-bed" />
        {Array.from({ length: 6 }, (_, index) => {
          const rackX = 10 + index * 42;
          return (
            <g key={index}>
              <rect x={rackX} y={9} width={22} height={38} className="warehouse-zone-rack-band" />
              <path d={`M${rackX + 7.3} 9 V47 M${rackX + 14.6} 9 V47`} className="warehouse-zone-rack-spine" />
              <path d={`M${rackX - 3} 18 H${rackX + 25} M${rackX - 3} 27 H${rackX + 25} M${rackX - 3} 36 H${rackX + 25}`} className="warehouse-zone-rack-level" />
            </g>
          );
        })}
        <path d="M3 55 H265 M14 50 V60 M56 50 V60 M98 50 V60 M140 50 V60 M182 50 V60 M224 50 V60 M262 50 V60" className="warehouse-zone-access-aisle" />
        <path d="M258 17 H262 M258 23 H262" className="warehouse-zone-arrow-line" />
      </g>
      <text x={282} y={22} textAnchor="end" className="warehouse-zone-card-count">
        {zone.observedCount > 0 ? `${zone.observedCount.toLocaleString("zh-CN")} 库位` : "—"}
      </text>
      <g transform="translate(282 105)" className="warehouse-zone-card-arrow" aria-hidden="true">
        <path d="M-14 0 H0 M-5 -5 L0 0 L-5 5" />
      </g>
      <title>{`${zone.code} ${main ? "主库区" : "辅库区"} · ${zone.observedCount.toLocaleString("zh-CN")} 个真实库位`}</title>
    </g>
  );
}

const ZonesScene = memo(function ZonesScene({
  zones,
  activeZone,
  runAction,
  onOpenZone,
}: {
  zones: readonly WarehouseZoneSpec[];
  activeZone: string;
  runAction: (action: () => void) => void;
  onOpenZone: (zone: string) => void;
}) {
  const mainCount = zones.filter((zone) => zone.kind === "main").length;
  const auxiliaryCount = zones.length - mainCount;
  const standardLocations = zones.reduce((sum, zone) => sum + zone.observedCount, 0);
  return (
    <g className="scene-zones">
      <rect x={18} y={20} width={1664} height={1060} rx={5} className="warehouse-zones-panel" />
      <rect x={34} y={36} width={1632} height={1028} rx={2} className="warehouse-zones-panel-inner" />
      <g className="warehouse-zones-heading">
        <text x={70} y={88}>昆山大仓 · 库区平面图</text>
        <text x={70} y={113}>
          {zones.length} 个真实库区 · {mainCount} 个主库区 · {auxiliaryCount} 个辅库区 · 点击库区查看主通道
        </text>
        <text x={1630} y={88} textAnchor="end">WAREHOUSE / ZONE SELECT · A 区最近出库口</text>
        <text x={1630} y={113} textAnchor="end">
          {zones.length} ZONES + {WAREHOUSE_SPECIAL_LOCATIONS.length} SPECIAL · {WAREHOUSE_LOCATION_COUNT.toLocaleString("zh-CN")} LOCATIONS
        </text>
      </g>
      <g className="warehouse-zone-axis">
        <path d="M318 146 V900 M318 146 H1637" />
        <text x={330} y={140}>仓库坐标 / NORTH</text>
        <text x={1630} y={140} textAnchor="end">4 列 × 5 行标准库区 · 比例示意</text>
      </g>
      <rect x={322} y={158} width={1294} height={736} rx={2} className="warehouse-zones-footprint" />
      <path
        d="M326 162 H1612 V890 H326 Z M330 166 V886 H1608 V166 Z"
        className="warehouse-plan-wall"
      />
      <path
        d="M322 880 V900 M344 882 V900"
        className="warehouse-plan-gate"
      />
      <g className="warehouse-plan-grid" aria-hidden="true">
        {[466, 788, 1110, 1432].map((x) => <path key={`v-${x}`} d={`M${x} 166 V886`} />)}
        {[307, 446, 585, 724].map((y) => <path key={`h-${y}`} d={`M330 ${y} H1608`} />)}
      </g>
      {zones.map((zone) => (
        <ZoneCard
          key={zone.code}
          zone={zone}
          active={zone.code === activeZone}
          runAction={runAction}
          onOpenZone={onOpenZone}
        />
      ))}
      <g
        transform="translate(58 238)"
        className="warehouse-special-entry"
        role="button"
        tabIndex={0}
        aria-label={`打开 ${WAREHOUSE_SPECIAL_LOCATIONS.length} 个特殊库位`}
        onClick={() => runAction(() => onOpenZone(WAREHOUSE_SPECIAL_ZONE))}
        onKeyDown={(event) => activateWithKeyboard(event, () => onOpenZone(WAREHOUSE_SPECIAL_ZONE))}
      >
        <rect width={232} height={106} rx={2} />
        <rect width={4} height={106} className="warehouse-special-accent" />
        <text x={20} y={28} className="warehouse-special-kicker">SPECIAL LOCATIONS</text>
        <text x={20} y={52} className="warehouse-special-title">特殊库位</text>
        <text x={20} y={72}>{WAREHOUSE_SPECIAL_LOCATIONS.length} 个非标准名称</text>
        <text x={20} y={89}>点击查看库存所在库位</text>
        <path d="M193 20 H207 M202 15 L207 20 L202 25" className="warehouse-special-arrow" />
      </g>
      <g transform="translate(284 818)" className="warehouse-zones-outbound">
        <rect x={-24} y={-18} width={48} height={36} rx={2} />
        <path d="M-14 -10 H14 M-14 0 H14 M-14 10 H14" />
        <path d="M25 0 H54" />
        <text x={0} y={43} textAnchor="middle">出库口</text>
        <text x={0} y={56} textAnchor="middle">OUTBOUND</text>
      </g>
      <g className="warehouse-zones-legend" transform="translate(72 430)">
        <text>库区图例</text>
        <rect y={14} width={8} height={28} rx={1} className="legend-main-zone" />
        <text x={17} y={28}>主库区 / 单字母</text>
        <text x={17} y={41}>靠近出库口的 A 区优先</text>
        <rect y={59} width={8} height={28} rx={1} className="legend-aux-zone" />
        <text x={17} y={73}>辅库区 / 双字符</text>
        <text x={17} y={86}>统一复用 Q 型布局</text>
      </g>
      <g className="warehouse-zones-north" transform="translate(72 958)">
        <path d="M0 6 V-15 M-6 -6 L0 -15 L6 -6" />
        <text y={24} textAnchor="middle">N</text>
      </g>
      <text x={1630} y={1026} textAnchor="end" className="warehouse-zones-footnote">
        名称总汇共 {WAREHOUSE_LOCATION_COUNT.toLocaleString("zh-CN")} 个库位 · 标准库位 {standardLocations.toLocaleString("zh-CN")} · 特殊库位 {WAREHOUSE_SPECIAL_LOCATIONS.length} · 仅显示已收录库位
      </text>
    </g>
  );
});

const SPECIAL_GRID = {
  x: 88,
  y: 214,
  width: 468,
  height: 88,
  columnStep: 508,
  rowStep: 103,
} as const;

const SpecialLocationsScene = memo(function SpecialLocationsScene({
  layout,
  runAction,
  onOpenRack,
}: {
  layout: WarehouseMapLayout;
  runAction: (action: () => void) => void;
  onOpenRack: (node: LocationNode) => void;
}) {
  return (
    <g className="scene-special">
      <rect x={18} y={20} width={1664} height={1060} rx={28} className="warehouse-zones-panel" />
      <rect x={34} y={36} width={1632} height={1028} rx={20} className="warehouse-zones-panel-inner" />
      <g className="warehouse-zones-heading">
        <text x={70} y={88}>特殊库位 · 名称总汇全量</text>
        <text x={70} y={113}>名称总汇中的 {layout.nodes.length} 个非标准库位 · 点击名称进入货架与库存详情</text>
        <text x={1630} y={88} textAnchor="end">SPECIAL LOCATION INDEX</text>
        <text x={1630} y={113} textAnchor="end">{layout.nodes.length} RECORDED LOCATIONS</text>
      </g>
      {layout.nodes.map((node, index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);
        const x = SPECIAL_GRID.x + column * SPECIAL_GRID.columnStep;
        const y = SPECIAL_GRID.y + row * SPECIAL_GRID.rowStep;
        return (
          <g
            key={node.code}
            transform={`translate(${x} ${y})`}
            className={`warehouse-special-location ${stockClass(node.stockState)}`}
            role="button"
            tabIndex={0}
            aria-label={`${node.locationName}，${stockStateLabel(node.stockState)}，打开库存详情`}
            onClick={() => runAction(() => onOpenRack(node))}
            onKeyDown={(event) => activateWithKeyboard(event, () => onOpenRack(node))}
          >
            <rect width={SPECIAL_GRID.width} height={SPECIAL_GRID.height} rx={10} className="warehouse-special-location-shell" />
            <text x={18} y={25} className="warehouse-special-index">{String(index + 1).padStart(2, "0")}</text>
            <text x={48} y={32} className="warehouse-special-name">{node.locationName}</text>
            <circle cx={470} cy={22} r={4} className="warehouse-special-state" />
            <text x={18} y={60} className="warehouse-special-meta">
              {node.stockState === "unknown" ? "等待库存关联" : stockStateLabel(node.stockState)}
              {node.skuCount ? ` · ${node.skuCount} SKU` : " · 实体库位"}
            </text>
            <path d="M422 63 H440 M433 56 L440 63 L433 70" className="warehouse-special-location-arrow" />
          </g>
        );
      })}
      <text x={1630} y={1026} textAnchor="end" className="warehouse-zones-footnote">
        特殊库位直接显示名称总汇原文；标准库位继续按 库区 → 主通道 → 小通道 → 货架 → 货物 五级下钻
      </text>
    </g>
  );
});

const MainAisleScene = memo(function MainAisleScene({
  layout,
  selection,
  filter,
  recommendedSmallAisles,
  runAction,
  onOpenSmall,
}: {
  layout: WarehouseMapLayout;
  selection: WarehouseSelection;
  filter: LocationFilter;
  recommendedSmallAisles: ReadonlySet<string>;
  runAction: (action: () => void) => void;
  onOpenSmall: (mainAisle: string, smallAisle: string) => void;
}) {
  const mainNumber = selection.mainAisle ?? layout.mainAisles[0]?.number ?? "01";
  const main = layout.mainAisles.find((item) => item.number === mainNumber);
  if (!main) return null;
  const branches = layout.smallAisles
    .filter((item) => item.mainAisle === mainNumber)
    .sort((a, b) => b.branchY - a.branchY);
  return (
    <g className="scene-main">
      <WarehousePerimeter layout={layout} detailed />
      <rect
        x={main.x - 200}
        y={112}
        width={400}
        height={815}
        rx={2}
        className="warehouse-focus-zone"
      />
      <g className="warehouse-focus-title" transform={`translate(${main.x} 138)`}>
        <text textAnchor="middle">{main.code} · 主通道</text>
        <text y={21} textAnchor="middle">{main.smallAisles} 条小通道汇入 · 01 号靠近出货口</text>
      </g>
      {branches.map((branch) => (
        <MiniBranch
          key={branch.code}
          branch={branch}
          filter={filter}
          active={branch.number === selection.smallAisle}
          recommended={recommendedSmallAisles.has(`${branch.mainAisle}-${branch.number}`)}
          interactive
          onActivate={() => runAction(() => onOpenSmall(branch.mainAisle, branch.number))}
        />
      ))}
      <MainLane aisle={main} interactive={false} label />
      <g className="main-aisle-columns" aria-hidden="true">
        {[230, 410, 590, 770].map((y) => (
          <g key={y} transform={`translate(${main.x} ${y})`}>
            <rect x={-5} y={-5} width={10} height={10} />
            <circle r={1.6} />
          </g>
        ))}
      </g>
      <g className="warehouse-side-key" transform={`translate(${main.x - 170} 946)`}>
        <text>左侧作业面 · SMALL AISLES</text>
      </g>
      <g className="warehouse-side-key" transform={`translate(${main.x + 170} 946)`}>
        <text textAnchor="end">右侧作业面 · SMALL AISLES</text>
      </g>
      <g transform={`translate(${main.x} 1000)`} className="warehouse-main-route">
        <path d={`M0 -24 V23 H${112 - main.x}`} />
        <circle cx={112 - main.x} cy={23} r={6} />
        <text x={112 - main.x - 12} y={27} textAnchor="end">往出货口</text>
      </g>
    </g>
  );
});

function rackCardPositions(count: number) {
  if (count <= 4) {
    const positions = [
      { x: 330, y: 206 },
      { x: 770, y: 206 },
      { x: 330, y: 420 },
      { x: 770, y: 420 },
    ];
    return positions.slice(0, count);
  }
  const columns = 4;
  return Array.from({ length: count }, (_, index) => ({
    x: 182 + (index % columns) * 248,
    y: 174 + Math.floor(index / columns) * 84,
  }));
}

const SmallAisleScene = memo(function SmallAisleScene({
  layout,
  selection,
  filter,
  products,
  productNamesByLocation,
  recommendedLocations,
  runAction,
  onOpenRack,
}: {
  layout: WarehouseMapLayout;
  selection: WarehouseSelection;
  filter: LocationFilter;
  products: Map<string, ProductAnalysis[]>;
  productNamesByLocation: ReadonlyMap<string, readonly string[]>;
  recommendedLocations: ReadonlySet<string>;
  runAction: (action: () => void) => void;
  onOpenRack: (node: LocationNode) => void;
}) {
  const mainNumber = selection.mainAisle ?? "01";
  const smallNumber = selection.smallAisle ?? "01";
  const racks = racksFor(layout, mainNumber, smallNumber);
  const selected = racks.find((node) => node.code === selection.selectedCode) ?? racks[0];
  const positions = rackCardPositions(racks.length);
  return (
    <g className="scene-small">
      <defs>
        <linearGradient id="aisle-floor-light" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#17231e" />
          <stop offset="1" stopColor="#0c1511" />
        </linearGradient>
        <pattern id="small-floor-grid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M24 0 H0 V24" fill="none" stroke="rgba(185,207,194,.055)" strokeWidth=".7" />
          <circle cx="0" cy="0" r=".8" fill="rgba(185,207,194,.12)" />
        </pattern>
      </defs>
      <rect x={44} y={46} width={1012} height={532} rx={4} className="warehouse-slab" />
      <rect x={60} y={62} width={980} height={500} rx={2} className="warehouse-slab-inner" />
      <rect x={76} y={80} width={948} height={464} rx={1} fill="url(#aisle-floor-light)" className="small-view-floor" />
      <rect x={76} y={80} width={948} height={464} fill="url(#small-floor-grid)" className="small-view-grid" />
      <g className="warehouse-focus-title" transform="translate(550 116)">
        <text textAnchor="middle">{layout.zone}{mainNumber}-{smallNumber} · 小通道</text>
        <text y={22} textAnchor="middle">
          {racks.length} 个真实货架 · 点击任一货架查看库存、批次与效期
        </text>
      </g>
      <rect x={104} y={294} width={880} height={58} rx={2} className="small-corridor-bed" />
      <rect x={126} y={308} width={836} height={30} rx={1} className="small-corridor-surface" />
      <path d="M112 302 H976 M112 344 H976" className="small-corridor-edge" />
      <path d="M146 323 H930" className="small-corridor-centreline" />
      <path d="M150 323 H260" className="small-route-highlight" />
      <path d="M226 318 L238 323 L226 328" className="small-route-arrow" />
      <path d="M520 318 L532 323 L520 328" className="small-route-arrow" />
      <path d="M814 318 L826 323 L814 328" className="small-route-arrow" />
      <rect x={110} y={315} width={16} height={16} rx={1} className="small-main-port" />
      <path d="M114 323 H122 M118 319 V327" className="small-main-port-mark" />
      <text x={118} y={360} textAnchor="middle" className="small-main-port-label">汇入主通道</text>
      {racks.map((node, index) => {
        const position = positions[index];
        const matched = filterMatches(node, filter);
        const selectedHere = node.code === selected?.code;
        const recommended = recommendedLocations.has(node.code);
        const names = productNamesByLocation.get(node.code);
        const displayProduct = productLabel(names);
        const fullProduct = productTitle(names);
        return (
          <g
            key={node.code}
            transform={`translate(${position.x} ${position.y})`}
            className={[
              "small-rack-card",
              stateClass(node),
              selectedHere ? "is-selected" : "",
              recommended ? "is-recommended" : "",
              matched ? "" : "is-dim",
            ].join(" ")}
            role="button"
            tabIndex={0}
            aria-label={`${node.code}，${stockStateLabel(node.stockState)}，距出货口 ${node.distance.toFixed(1)} 米${fullProduct ? `，在库 ${fullProduct}` : ""}`}
            onClick={() => runAction(() => onOpenRack(node))}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              runAction(() => onOpenRack(node));
            }}
          >
            <rect x={-122} y={-39} width={244} height={78} rx={2} className="small-rack-shadow" />
            <rect x={-117} y={-35} width={234} height={70} rx={2} className="small-rack-body" />
            <path d="M-104 -27 H104" className="small-rack-topline" />
            <path d="M-104 -27 V27 M-62 -27 V27 M-20 -27 V27 M22 -27 V27 M64 -27 V27 M104 -27 V27" className="small-rack-standard" />
            <path d="M-104 -9 H104 M-104 21 H104" className="small-rack-bay-line" />
            <path d="M-113 -28 H-108 V28 H-113 M108 -28 H113 V28 H108" className="small-rack-endcap" />
            <rect x={104} y={-31} width={5} height={9} rx={1} className="small-rack-state-dot" />
            {recommended && (
              <g className="small-rack-recommendation-badge" transform="translate(-108 -36)">
                <rect x={0} y={0} width={54} height={14} rx={1.5} />
                <text x={27} y={10} textAnchor="middle">推荐移入</text>
              </g>
            )}
            <text x={-98} y={4} className="small-rack-code">{node.code}</text>
            <text x={-98} y={24} className="small-rack-meta">
              {stockStateLabel(node.stockState)} · 距门 {node.distance.toFixed(1)} m
            </text>
            <text x={98} y={-19} textAnchor="end" className="small-rack-number">{node.rack}</text>
            {displayProduct && (
              <g className="small-rack-product-tag">
                <rect x={4} y={11} width={104} height={17} rx={1.5} />
                <text x={96} y={23} textAnchor="end">{displayProduct}</text>
              </g>
            )}
            <title>{`${node.code} · ${stockStateLabel(node.stockState)} · ${node.distance.toFixed(1)} m${fullProduct ? ` · 在库 ${fullProduct}` : ""}${recommended ? " · 效期策略推荐移入" : ""}`}</title>
          </g>
        );
      })}
      <g className="rack-dimension-mark" transform="translate(330 462)">
        <path d="M-117 0 H117 M-117 -6 V6 M117 -6 V6" />
        <text y={20} textAnchor="middle">2.0 m</text>
      </g>
      <g className="rack-dimension-mark" transform="translate(900 420)">
        <path d="M0 -35 V35 M-6 -35 H6 M-6 35 H6" />
        <text x={14} y={4}>0.6 m</text>
      </g>
      <g className="small-view-legend" transform="translate(96 506)">
        <rect width={10} height={10} rx={3} className="legend-stocked" />
        <text x={17} y={8.5}>有货</text>
        <rect x={72} width={10} height={10} rx={3} className="legend-empty" />
        <text x={89} y={8.5}>空架</text>
        <rect x={144} width={10} height={10} rx={3} className="legend-unknown" />
        <text x={161} y={8.5}>待核对</text>
      </g>
      <g className="small-view-reference" transform="translate(956 506)" textAnchor="end">
        <text>点击货架进入单架正视图 · Esc 返回本层</text>
      </g>
    </g>
  );
});

const RackScene = memo(function RackScene({
  layout,
  node,
  products,
  productNames,
  batches,
  recommended,
}: {
  layout: WarehouseMapLayout;
  node: LocationNode;
  products: ProductAnalysis[];
  productNames: readonly string[];
  batches: BatchRecord[];
  recommended: boolean;
}) {
  const zoneSpec = warehouseZone(layout.zone);
  const primaryProduct = products[0]?.product
    ?? productNames[0]
    ?? batches[0]?.product
    ?? node.locationName;
  const fillCount = node.stockState === "stocked"
    ? Math.max(2, Math.min(8, node.batchCount + node.skuCount))
    : 0;
  return (
    <g className="scene-rack">
      <defs>
        <linearGradient id="rack-hero-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#11271c" />
          <stop offset="1" stopColor="#07100c" />
        </linearGradient>
        <linearGradient id="rack-steel" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#75877d" />
          <stop offset=".18" stopColor="#c4d4ca" />
          <stop offset=".5" stopColor="#8ca096" />
          <stop offset=".82" stopColor="#d6e3dc" />
          <stop offset="1" stopColor="#66786f" />
        </linearGradient>
      </defs>
      <rect x={34} y={36} width={1032} height={548} rx={24} className="warehouse-slab" />
      <rect x={50} y={52} width={1000} height={516} rx={17} className="warehouse-slab-inner" />
      <rect x={66} y={68} width={968} height={484} rx={12} fill="url(#rack-hero-floor)" className="rack-hero-floor" />
      <g className="rack-hero-heading" transform="translate(92 108)">
        <text>{node.code}</text>
        <text y={24}>
          原始编码 {node.rawCode} · {node.locationType || "库位"} · {zoneSpec?.kind === "auxiliary" ? "辅库区" : "主库区"}
        </text>
        {recommended && (
          <g className="rack-recommendation-flag" transform="translate(804 -20)">
            <rect width={126} height={30} rx={15} />
            <text x={63} y={20} textAnchor="middle">效期策略推荐移入</text>
          </g>
        )}
      </g>

      <g className="rack-front-elevation" transform="translate(92 180)">
        <text x={0} y={-18} className="rack-panel-eyebrow">货架正视图 / RACK FRONT VIEW</text>
        <rect x={0} y={0} width={438} height={302} rx={14} className="rack-front-shell" />
        <rect x={19} y={20} width={400} height={260} rx={7} className="rack-front-inner" />
        {[0, 1, 2, 3].map((tier) => (
          <g key={tier} transform={`translate(19 ${50 + tier * 62})`}>
            <rect width={400} height={62} className="rack-front-tier" />
            <path d="M0 56 H400" className="rack-front-beam" />
            {[0, 1].map((bay) => (
              <g key={bay} transform={`translate(${18 + bay * 198} 0)`}>
                {Array.from({ length: 8 }, (_, box) => {
                  const visible = tier * 2 + bay < fillCount;
                  return (
                    <rect
                      key={box}
                      x={(box % 4) * 42}
                      y={box < 4 ? 8 : 31}
                      width={36}
                      height={20}
                      rx={3}
                      className={`rack-stock-box ${visible ? `is-filled stock-${node.stockState}` : "is-empty"}`}
                    />
                  );
                })}
              </g>
            ))}
          </g>
        ))}
        <rect x={0} y={0} width={9} height={302} rx={4} fill="url(#rack-steel)" />
        <rect x={429} y={0} width={9} height={302} rx={4} fill="url(#rack-steel)" />
        <circle cx={213} cy={291} r={5} className="rack-front-state" />
        <text x={224} y={295} className="rack-front-state-label">{stockStateLabel(node.stockState)}</text>
      </g>

      <g className="rack-detail-cards" transform="translate(560 162)">
        <g className="rack-detail-card">
          <rect width={420} height={104} rx={11} />
          <text x={20} y={26} className="rack-card-label">库位身份</text>
          <text x={20} y={50} className="rack-card-value">{node.code}</text>
          <text x={20} y={74} className="rack-card-meta">
            {layout.zone} 区 · {node.mainAisle || "—"} 号主通道 · {node.smallAisle || "—"} 号小通道
          </text>
          <text x={20} y={91} className="rack-card-meta">
            货架 {node.rack || "—"} · {node.encoded ? "实体库位" : "虚拟库位"}
          </text>
        </g>
        <g className="rack-detail-card" transform="translate(0 118)">
          <rect width={420} height={104} rx={11} />
          <text x={20} y={26} className="rack-card-label">库存快照</text>
          <text x={20} y={56} className="rack-card-number">{node.stockQuantity.toLocaleString("zh-CN")}</text>
          <text x={86} y={55} className="rack-card-unit">件</text>
          <text x={232} y={42} className="rack-card-kpi">SKU {node.skuCount}</text>
          <text x={232} y={64} className="rack-card-kpi">批次 {node.batchCount}</text>
          <text x={20} y={86} className="rack-card-meta">
            最近更新 {node.batchCount ? `${node.batchCount} 个有效批次` : "暂无有效批次"} · 距出库口 {node.distance.toFixed(1)} m
          </text>
        </g>
        <g className="rack-detail-card" transform="translate(0 236)">
          <rect width={420} height={104} rx={11} />
          <text x={20} y={26} className="rack-card-label">在库与批次</text>
          {products.length || productNames.length || batches.length ? (
            <>
              <text x={20} y={50} className="rack-card-value rack-card-product">{primaryProduct}</text>
              <text x={20} y={73} className="rack-card-meta">
                {products[0]?.sku ?? batches[0]?.sku ?? "SKU 待核对"}
                {batches[0] ? ` · 批次 ${batches[0].lot} · 到期 ${batches[0].expiresOn || "待核对"}` : " · 批次与效期待核对"}
              </text>
              <text x={20} y={92} className="rack-card-meta">
                {batches.length > 1 ? `另有 ${batches.length - 1} 个批次，可在下方检查器查看完整明细` : "路线按真实货架拣货边计算"}
              </text>
            </>
          ) : (
            <>
              <text x={20} y={54} className="rack-card-value">{node.stockState === "empty" ? "空架" : "库存待核对"}</text>
              <text x={20} y={78} className="rack-card-meta">当前库位没有关联在库品项或有效批次。</text>
              <text x={20} y={94} className="rack-card-meta">导入库存数据后，商品、批次、效期将在此同步。</text>
            </>
          )}
        </g>
      </g>

      <g className="rack-route-strip" transform="translate(92 516)">
        <path d="M0 18 H824" />
        <circle cx={0} cy={18} r={7} />
        <circle cx={824} cy={18} r={7} />
        <text x={0} y={0}>货架拣货边</text>
        <text x={412} y={0} textAnchor="middle">小通道 → 主通道 → 分发走廊</text>
        <text x={824} y={0} textAnchor="end">出货口</text>
        <text x={412} y={44} textAnchor="middle">{node.distance.toFixed(1)} m · 真实路线距离</text>
      </g>
    </g>
  );
});

const FullWarehouseScene = memo(function FullWarehouseScene({
  layout,
  selectedCode,
  detailed,
  runAction,
  onOpenMain,
}: {
  layout: WarehouseMapLayout;
  selectedCode: string | null;
  detailed: boolean;
  runAction: (action: () => void) => void;
  onOpenMain: (mainAisle: string) => void;
}) {
  return (
    <g className="scene-full">
      <WarehousePerimeter layout={layout} detailed />
      <path d="M112 1000 V941 H1490" className="warehouse-dispatch-path" />
      <g transform="translate(112 1000)" className="warehouse-exit-node">
        <circle r={27} className="warehouse-exit-halo" />
        <circle r={11} className="warehouse-exit-core" />
        <text y={44} textAnchor="middle">出货口</text>
      </g>
      {layout.mainAisles.map((aisle) => (
        <g
          key={aisle.code}
          className="warehouse-density-cluster"
          role="button"
          tabIndex={0}
          aria-label={`打开 ${aisle.code} 主通道，聚合显示 ${aisle.observedCount} 个货架`}
          onClick={() => runAction(() => onOpenMain(aisle.number))}
          onKeyDown={(event) => activateWithKeyboard(event, () => onOpenMain(aisle.number))}
        >
          <rect
            x={aisle.x - 55}
            y={aisle.top + 44}
            width={110}
            height={aisle.height - 100}
            rx={18}
          />
          {Array.from({ length: Math.min(14, Math.max(3, Math.ceil(aisle.observedCount / 80))) }, (_, item) => (
            <path
              key={item}
              d={`M${aisle.x - 38} ${aisle.top + 68 + item * 42} H${aisle.x + 38}`}
            />
          ))}
          <text x={aisle.x} y={aisle.top + 27} textAnchor="middle" className="warehouse-density-count">
            {aisle.observedCount.toLocaleString("zh-CN")}
          </text>
          <text x={aisle.x} y={aisle.top + 40} textAnchor="middle" className="warehouse-density-label">
            货架
          </text>
          <title>{`${aisle.code} 主通道 · ${aisle.smallAisles} 条小通道 · ${aisle.observedCount} 个货架聚合显示`}</title>
        </g>
      ))}
      {layout.mainAisles.map((aisle) => (
        <MainLane
          key={`lane-${aisle.code}`}
          aisle={aisle}
          interactive={false}
          label
        />
      ))}
      <g className="warehouse-overview-title">
        <text x={255} y={128}>{layout.zone} · 库区全景</text>
        <text x={255} y={149}>
          {layout.stats.mainAisles} 主通道 · {layout.stats.smallAisles} 小通道 · {layout.stats.racks.toLocaleString("zh-CN")} 货架按主通道聚合
        </text>
      </g>
      <g className="warehouse-route-axis">
        <path d="M150 940 H1500" />
        <text x={1308} y={926}>分发缓冲区 / DISPATCH CORRIDOR</text>
      </g>
      {selectedCode && (
        <g className="warehouse-full-selection-note" transform="translate(1284 164)">
          <rect width={244} height={58} rx={10} />
          <text x={16} y={23}>当前货架</text>
          <text x={16} y={44}>{selectedCode}</text>
        </g>
      )}
    </g>
  );
}, (prev, next) => (
  prev.layout === next.layout
  && prev.selectedCode === next.selectedCode
  && prev.detailed === next.detailed
  && prev.runAction === next.runAction
  && prev.onOpenMain === next.onOpenMain
));

export function WarehouseMap2D({
  layout,
  zones,
  selection,
  filter,
  products,
  productNamesByLocation,
  batchesByLocation,
  dataStatus = "STATIC READY",
  recommendedLocations = EMPTY_RECOMMENDED,
  onOpenZone,
  onOpenMain,
  onOpenSmall,
  onOpenRack,
  onBack,
}: WarehouseMap2DProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomReadoutRef = useRef<HTMLSpanElement>(null);
  const currentView = useRef<ViewBox>({ ...ZONES_VIEW });
  const targetView = useRef<ViewBox>({ ...ZONES_VIEW });
  const frameRef = useRef<number | null>(null);
  const animationRef = useRef<{
    start: number;
    from: ViewBox;
    to: ViewBox;
    duration: number;
  } | null>(null);
  const dragRef = useRef<{
    x0: number;
    y0: number;
    lastX: number;
    lastY: number;
    origin: ViewBox;
    rect: ViewportRect;
    moved: boolean;
    samples: Array<{ x: number; y: number; t: number }>;
    frame: number | null;
  } | null>(null);
  const inertiaRef = useRef<{
    vx: number;
    vy: number;
    origin: ViewBox;
    startedAt: number;
    lastAt: number;
    duration: number;
    frame: number | null;
  } | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{
    initialDistance: number;
    initialX: number;
    initialY: number;
    origin: ViewBox;
    rect: ViewportRect;
    moved: boolean;
    distance: number;
    centerX: number;
    centerY: number;
    frame: number | null;
  } | null>(null);
  const gestureRef = useRef<{
    initialScale: number;
    scale: number;
    initialX: number;
    initialY: number;
    clientX: number;
    clientY: number;
    origin: ViewBox;
    rect: ViewportRect;
    moved: boolean;
    frame: number | null;
  } | null>(null);
  const wheelRef = useRef<{
    factor: number;
    clientX?: number;
    clientY?: number;
    frame: number | null;
  } | null>(null);
  const zoomSettleRef = useRef<number | null>(null);
  const suppressClickUntilRef = useRef(0);
  const mountedRef = useRef(false);
  const detailedRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [detailed, setDetailed] = useState(false);

  const activeMain = layout.mainAisles.find((item) => item.number === selection.mainAisle);
  const selectedRack = selection.level === "rack"
    ? layout.nodeByCode.get(selection.selectedCode ?? "")
    : undefined;
  const mainZoneCount = zones.filter((zone) => zone.kind === "main").length;
  const auxiliaryZoneCount = zones.filter((zone) => zone.kind === "auxiliary").length;
  const observedZoneLocations = zones.reduce((sum, zone) => sum + zone.observedCount, 0);
  const hudTitle =
    selection.level === "zones"
      ? "全仓库区总览"
      : selection.level === "overview"
        ? layout.zone === WAREHOUSE_SPECIAL_ZONE
          ? "特殊库位总览"
          : `${layout.zone} 库区总览`
        : selection.level === "main"
          ? `${layout.zone}${selection.mainAisle ?? ""} 主通道`
          : selection.level === "small"
            ? `${layout.zone}${selection.mainAisle ?? ""}-${selection.smallAisle ?? ""} 小通道`
            : selection.level === "rack"
              ? `${selection.selectedCode ?? ""} 货架`
              : `${layout.zone} 完整库位图`;
  const hudMetrics = selection.level === "zones"
    ? [
      { label: "主库区", value: mainZoneCount, tone: "stocked" },
      { label: "辅库区", value: auxiliaryZoneCount, tone: "empty" },
      { label: "标准库位", value: observedZoneLocations, tone: "neutral" },
    ]
    : selectedRack
      ? [
        { label: "库存", value: selectedRack.stockQuantity, tone: selectedRack.stockState },
        { label: "SKU", value: selectedRack.skuCount, tone: "neutral" },
        { label: "批次", value: selectedRack.batchCount, tone: "neutral" },
      ]
      : [
        { label: "有货", value: layout.stats.stocked, tone: "stocked" },
        { label: "空架", value: layout.stats.empty, tone: "empty" },
        { label: "待核对", value: layout.stats.unknown, tone: "unknown" },
      ];
  const recommendedSmallAisles = useMemo(
    () => new Set(
      layout.nodes
        .filter((node) => recommendedLocations.has(node.code))
        .map((node) => `${node.mainAisle}-${node.smallAisle}`),
    ),
    [layout, recommendedLocations],
  );
  const baseView =
    selection.level === "zones"
      ? ZONES_VIEW
      : selection.level === "overview"
      ? OVERVIEW_VIEW
      : selection.level === "full"
        ? FULL_VIEW
        : selection.level === "main"
          ? mainView(activeMain)
          : selection.level === "small"
            ? SMALL_VIEW
            : RACK_VIEW;

  function applyView(box: ViewBox) {
    svgRef.current?.setAttribute("viewBox", viewBoxAttribute(box));
    if (zoomReadoutRef.current) {
      zoomReadoutRef.current.textContent = `${Math.round((baseView.width / box.width) * 100)}%`;
    }
  }

  function canvasForLevel(): ViewBox {
    if (selection.level === "small") return { x: 0, y: 0, width: 1100, height: 620 };
    if (selection.level === "rack") return { x: 0, y: 0, width: 1100, height: 620 };
    if (selection.level === "zones") return { x: 0, y: 0, width: 1700, height: 1100 };
    return { x: 18, y: 22, width: 1664, height: 1060 };
  }

  function clampView(box: ViewBox): ViewBox {
    const base = baseView;
    const canvas = canvasForLevel();
    const minWidth = base.width * 0.12;
    const maxWidth = base.width * 1.06;
    const width = Math.min(maxWidth, Math.max(minWidth, box.width));
    const height = width * (box.height / box.width);
    const minX = canvas.x - 28;
    const maxX = canvas.x + canvas.width - width + 28;
    const minY = canvas.y - 28;
    const maxY = canvas.y + canvas.height - height + 28;
    return {
      x: maxX < minX ? canvas.x + (canvas.width - width) / 2 : Math.min(Math.max(box.x, minX), maxX),
      y: maxY < minY ? canvas.y + (canvas.height - height) / 2 : Math.min(Math.max(box.y, minY), maxY),
      width,
      height,
    };
  }

  function finishView(next: ViewBox) {
    targetView.current = next;
    const nextDetailed = selection.level === "full"
      && next.width < baseView.width * 0.46;
    if (nextDetailed === detailedRef.current) return;
    detailedRef.current = nextDetailed;
    setDetailed(nextDetailed);
  }

  function step(now: number) {
    const animation = animationRef.current;
    if (!animation) return;
    const raw = (now - animation.start) / animation.duration;
    const progress = Math.min(1, raw);
    const eased = easeOutCubic(progress);
    const next = {
      x: animation.from.x + (animation.to.x - animation.from.x) * eased,
      y: animation.from.y + (animation.to.y - animation.from.y) * eased,
      width: animation.from.width + (animation.to.width - animation.from.width) * eased,
      height: animation.from.height + (animation.to.height - animation.from.height) * eased,
    };
    applyView(next);
    currentView.current = next;
    if (progress < 1) {
      frameRef.current = requestAnimationFrame(step);
      return;
    }
    frameRef.current = null;
    animationRef.current = null;
    currentView.current = { ...animation.to };
    containerRef.current?.classList.remove("is-animating");
    finishView(animation.to);
  }

  function animateTo(
    nextView: ViewBox,
    immediate = false,
    duration = ANIMATION_MS,
  ) {
    stopInertia();
    const next = clampView(nextView);
    targetView.current = next;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    animationRef.current = null;
    if (immediate || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      containerRef.current?.classList.remove("is-animating");
      currentView.current = next;
      applyView(next);
      finishView(next);
      return;
    }
    animationRef.current = {
      start: performance.now(),
      from: { ...currentView.current },
      to: next,
      duration,
    };
    containerRef.current?.classList.add("is-animating");
    frameRef.current = requestAnimationFrame(step);
  }

  function stopInertia() {
    const inertia = inertiaRef.current;
    if (inertia?.frame != null) cancelAnimationFrame(inertia.frame);
    inertiaRef.current = null;
  }

  function startInertia(drag: NonNullable<typeof dragRef.current>, rect: ViewportRect) {
    stopInertia();
    const samples = drag.samples;
    const end = samples[samples.length - 1];
    const cutoff = (end?.t ?? performance.now()) - 96;
    const start = [...samples].reverse().find((sample) => sample.t <= cutoff)
      ?? samples[0];
    if (!end || !start || end.t - start.t < 12) {
      containerRef.current?.classList.remove("is-animating");
      return;
    }
    const scale = Math.min(
      rect.width / targetView.current.width,
      rect.height / targetView.current.height,
    );
    if (!Number.isFinite(scale) || scale <= 0) return;
    let vx = -(end.x - start.x) / (end.t - start.t) / scale;
    let vy = -(end.y - start.y) / (end.t - start.t) / scale;
    const speed = Math.hypot(vx, vy);
    const maxSpeed = 2.4;
    if (speed > maxSpeed) {
      vx = (vx / speed) * maxSpeed;
      vy = (vy / speed) * maxSpeed;
    }
    if (speed < 0.035) {
      containerRef.current?.classList.remove("is-animating");
      return;
    }
    const now = performance.now();
    inertiaRef.current = {
      vx,
      vy,
      origin: { ...currentView.current },
      startedAt: now,
      lastAt: now,
      duration: 420,
      frame: null,
    };
    containerRef.current?.classList.add("is-animating");
    const inertiaStep = (frameTime: number) => {
      const inertia = inertiaRef.current;
      if (!inertia) return;
      const elapsed = Math.min(inertia.duration, frameTime - inertia.startedAt);
      const tau = inertia.duration / 5.2;
      const travel = tau * (1 - Math.exp(-elapsed / tau));
      const next = clampView({
        ...inertia.origin,
        x: inertia.origin.x + inertia.vx * travel,
        y: inertia.origin.y + inertia.vy * travel,
      });
      currentView.current = next;
      targetView.current = next;
      applyView(next);
      inertia.lastAt = frameTime;
      if (elapsed < inertia.duration) {
        inertia.frame = requestAnimationFrame(inertiaStep);
        return;
      }
      inertiaRef.current = null;
      containerRef.current?.classList.remove("is-animating");
      finishView(next);
    };
    inertiaRef.current.frame = requestAnimationFrame(inertiaStep);
  }

  function nextZoomView(factor: number, clientX?: number, clientY?: number) {
    return zoomViewAt(targetView.current, targetView.current.width * factor, clientX, clientY);
  }

  function readViewportRect() {
    const svg = svgRef.current;
    return svg ? normalizedRect(svg.getBoundingClientRect()) : null;
  }

  function clientPointInView(
    current: ViewBox,
    clientX: number,
    clientY: number,
    viewportRect?: ViewportRect,
  ) {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = viewportRect ?? readViewportRect();
    if (!rect) return null;
    if (!rect.width || !rect.height) return null;
    const scale = Math.min(rect.width / current.width, rect.height / current.height);
    const offsetX = (rect.width - current.width * scale) / 2;
    const offsetY = (rect.height - current.height * scale) / 2;
    return {
      x: current.x + (clientX - rect.left - offsetX) / scale,
      y: current.y + (clientY - rect.top - offsetY) / scale,
      ratioX: Math.max(0, Math.min(1, (clientX - rect.left - offsetX) / (current.width * scale))),
      ratioY: Math.max(0, Math.min(1, (clientY - rect.top - offsetY) / (current.height * scale))),
      scale,
    };
  }

  function zoomViewAt(
    current: ViewBox,
    requestedWidth: number,
    clientX?: number,
    clientY?: number,
    viewportRect?: ViewportRect,
  ) {
    const nextWidth = Math.min(
      baseView.width * 1.06,
      Math.max(baseView.width * 0.12, requestedWidth),
    );
    const ratio = nextWidth / current.width;
    let anchorX = current.x + current.width / 2;
    let anchorY = current.y + current.height / 2;
    if (clientX != null && clientY != null) {
      const anchor = clientPointInView(current, clientX, clientY, viewportRect);
      if (anchor) {
        anchorX = anchor.x;
        anchorY = anchor.y;
      }
    }
    return {
      x: anchorX - (anchorX - current.x) * ratio,
      y: anchorY - (anchorY - current.y) * ratio,
      width: nextWidth,
      height: current.height * ratio,
    };
  }

  function zoom(factor: number, clientX?: number, clientY?: number) {
    animateTo(nextZoomView(factor, clientX, clientY), false, ZOOM_ANIMATION_MS);
  }

  function applyWheelZoom(factor: number, clientX: number, clientY: number) {
    stopInertia();
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    animationRef.current = null;
    containerRef.current?.classList.remove("is-animating");
    const next = clampView(
      zoomViewAt(targetView.current, targetView.current.width * factor, clientX, clientY),
    );
    targetView.current = next;
    currentView.current = next;
    applyView(next);
    finishView(next);
  }

  function queueWheelZoom(factor: number, clientX: number, clientY: number) {
    containerRef.current?.classList.add("is-animating");
    if (zoomSettleRef.current != null) window.clearTimeout(zoomSettleRef.current);
    zoomSettleRef.current = window.setTimeout(() => {
      zoomSettleRef.current = null;
      containerRef.current?.classList.remove("is-animating");
    }, 120);
    if (wheelRef.current) {
      wheelRef.current.factor *= factor;
      wheelRef.current.clientX = clientX;
      wheelRef.current.clientY = clientY;
      return;
    }
    wheelRef.current = { factor, clientX, clientY, frame: null };
    wheelRef.current.frame = requestAnimationFrame(() => {
      const pending = wheelRef.current;
      if (!pending) return;
      wheelRef.current = null;
      applyWheelZoom(pending.factor, pending.clientX!, pending.clientY!);
    });
  }

  function flushGestureZoom() {
    const gesture = gestureRef.current;
    if (!gesture) return;
    gesture.frame = null;
    const next = clampView(
      zoomViewAt(
        gesture.origin,
        gesture.origin.width * (gesture.initialScale / gesture.scale),
        gesture.initialX,
        gesture.initialY,
        gesture.rect,
      ),
    );
    targetView.current = next;
    currentView.current = next;
    applyView(next);
  }

  function flushPinchView() {
    const pinch = pinchRef.current;
    if (!pinch) return;
    pinch.frame = null;
    const anchor = clientPointInView(
      pinch.origin,
      pinch.initialX,
      pinch.initialY,
      pinch.rect,
    );
    if (!anchor) return;
    const scale = Math.max(0.01, pinch.distance / pinch.initialDistance);
    const nextWidth = pinch.origin.width / scale;
    const nextHeight = pinch.origin.height / scale;
    const viewportScale = Math.min(
      pinch.rect.width / nextWidth,
      pinch.rect.height / nextHeight,
    );
    const next = clampView({
      x: anchor.x - anchor.ratioX * nextWidth
        - (pinch.centerX - pinch.initialX) / viewportScale,
      y: anchor.y - anchor.ratioY * nextHeight
        - (pinch.centerY - pinch.initialY) / viewportScale,
      width: nextWidth,
      height: nextHeight,
    });
    targetView.current = next;
    currentView.current = next;
    applyView(next);
  }

  const runAction = useCallback((action: () => void) => {
    if (performance.now() < suppressClickUntilRef.current) return;
    action();
  }, []);

  useEffect(() => {
    animateTo(baseView, !mountedRef.current);
    mountedRef.current = true;
    // Scene view changes are driven by the active zone, level and main aisle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.zone, selection.level, selection.mainAisle]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handleNativeWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      if (Math.abs(event.deltaY) < 0.2) return;
      const normalizedDelta = Math.max(-180, Math.min(180, event.deltaY));
      const factor = Math.exp(normalizedDelta * 0.00165);
      queueWheelZoom(factor, event.clientX, event.clientY);
    };
    type SafariGestureEvent = Event & {
      scale: number;
      clientX: number;
      clientY: number;
    };
    const finishGesture = () => {
      const gesture = gestureRef.current;
      if (!gesture) return;
      if (gesture.frame != null) {
        cancelAnimationFrame(gesture.frame);
        flushGestureZoom();
      }
      if (gesture.moved) {
        suppressClickUntilRef.current = performance.now() + 350;
        finishView(targetView.current);
      }
      gestureRef.current = null;
      setDragging(false);
    };
    const handleGestureStart = (rawEvent: Event) => {
      const event = rawEvent as SafariGestureEvent;
      event.preventDefault();
      event.stopPropagation();
      stopInertia();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      animationRef.current = null;
      containerRef.current?.classList.remove("is-animating");
      const rect = readViewportRect();
      if (!rect) return;
      gestureRef.current = {
        initialScale: Math.max(0.01, event.scale || 1),
        scale: event.scale || 1,
        initialX: event.clientX,
        initialY: event.clientY,
        clientX: event.clientX,
        clientY: event.clientY,
        origin: { ...targetView.current },
        rect,
        moved: false,
        frame: null,
      };
      setDragging(true);
    };
    const handleGestureChange = (rawEvent: Event) => {
      const event = rawEvent as SafariGestureEvent;
      event.preventDefault();
      event.stopPropagation();
      const gesture = gestureRef.current;
      if (!gesture) return;
      gesture.scale = Math.max(0.01, event.scale || 1);
      gesture.clientX = event.clientX;
      gesture.clientY = event.clientY;
      if (Math.abs(gesture.scale - gesture.initialScale) > 0.005) gesture.moved = true;
      if (gesture.frame == null) {
        gesture.frame = requestAnimationFrame(flushGestureZoom);
      }
    };
    const handleGestureEnd = (rawEvent: Event) => {
      rawEvent.preventDefault();
      rawEvent.stopPropagation();
      finishGesture();
    };
    node.addEventListener("wheel", handleNativeWheel, { passive: false });
    node.addEventListener("gesturestart", handleGestureStart, { passive: false });
    node.addEventListener("gesturechange", handleGestureChange, { passive: false });
    node.addEventListener("gestureend", handleGestureEnd, { passive: false });
    return () => {
      node.removeEventListener("wheel", handleNativeWheel);
      node.removeEventListener("gesturestart", handleGestureStart);
      node.removeEventListener("gesturechange", handleGestureChange);
      node.removeEventListener("gestureend", handleGestureEnd);
    };
    // Listener is rebound when the active coordinate system changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.zone, selection.level, selection.mainAisle]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (selection.level !== "zones") onBack();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBack, selection.level]);

  useEffect(() => () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    if (dragRef.current?.frame) cancelAnimationFrame(dragRef.current.frame);
    if (pinchRef.current?.frame) cancelAnimationFrame(pinchRef.current.frame);
    if (inertiaRef.current?.frame) cancelAnimationFrame(inertiaRef.current.frame);
    if (gestureRef.current?.frame) cancelAnimationFrame(gestureRef.current.frame);
    if (wheelRef.current?.frame) cancelAnimationFrame(wheelRef.current.frame);
    if (zoomSettleRef.current != null) window.clearTimeout(zoomSettleRef.current);
    animationRef.current = null;
  }, []);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    if ((event.target as Element).closest(".map-zoom-controls")) return;
    event.preventDefault();
    stopInertia();
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    suppressClickUntilRef.current = 0;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    animationRef.current = null;
    const rect = readViewportRect();
    if (!rect) return;
    if (pointersRef.current.size >= 2) {
      const points = [...pointersRef.current.values()];
      const [a, b] = points.slice(-2);
      const centerX = (a.x + b.x) / 2;
      const centerY = (a.y + b.y) / 2;
      const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      pinchRef.current = {
        initialDistance: distance,
        initialX: centerX,
        initialY: centerY,
        origin: { ...targetView.current },
        rect,
        moved: false,
        distance,
        centerX,
        centerY,
        frame: null,
      };
      dragRef.current = null;
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    } else {
      dragRef.current = {
        x0: event.clientX,
        y0: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        origin: { ...targetView.current },
        rect,
        moved: false,
        samples: [{ x: event.clientX, y: event.clientY, t: performance.now() }],
        frame: null,
      };
    }
  }

  function flushDragPosition() {
    const drag = dragRef.current;
    if (!drag) return;
    drag.frame = null;
    const rect = drag.rect;
    if (!rect.width || !rect.height) return;
    const dx = drag.lastX - drag.x0;
    const dy = drag.lastY - drag.y0;
    const scale = Math.min(
      rect.width / drag.origin.width,
      rect.height / drag.origin.height,
    );
    const next = clampView({
      ...drag.origin,
      x: drag.origin.x - dx / scale,
      y: drag.origin.y - dy / scale,
    });
    currentView.current = next;
    targetView.current = next;
    applyView(next);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      event.preventDefault();
      const points = [...pointersRef.current.values()];
      const [a, b] = points.slice(-2);
      const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      const centerX = (a.x + b.x) / 2;
      const centerY = (a.y + b.y) / 2;
      pinch.distance = distance;
      pinch.centerX = centerX;
      pinch.centerY = centerY;
      pinch.moved = pinch.moved || Math.abs(distance - pinch.initialDistance) > 3
        || Math.hypot(centerX - pinch.initialX, centerY - pinch.initialY) > 3;
      if (pinch.frame == null) {
        pinch.frame = requestAnimationFrame(flushPinchView);
      }
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x0;
    const dy = event.clientY - drag.y0;
    if (Math.hypot(dx, dy) < 5 && !drag.moved) return;
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    drag.samples.push({ x: event.clientX, y: event.clientY, t: performance.now() });
    const sampleCutoff = performance.now() - 110;
    while (drag.samples.length > 2 && drag.samples[1].t < sampleCutoff) {
      drag.samples.shift();
    }
    if (drag.frame == null) {
      drag.frame = requestAnimationFrame(flushDragPosition);
    }
  }

  function handlePointerEnd(event: ReactPointerEvent<HTMLDivElement>) {
    const pinch = pinchRef.current;
    const viewportRect = pinch?.rect ?? dragRef.current?.rect ?? readViewportRect();
    if (pinch?.frame != null) {
      cancelAnimationFrame(pinch.frame);
      flushPinchView();
    }
    pointersRef.current.delete(event.pointerId);
    const drag = dragRef.current;
    if (drag?.frame != null) {
      cancelAnimationFrame(drag.frame);
      flushDragPosition();
    }
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const pinchMoved = pinch?.moved ?? false;
    if (drag?.moved || pinchMoved) {
      suppressClickUntilRef.current = performance.now() + 350;
    }
    if (drag?.moved || pinchMoved) {
      finishView(targetView.current);
      if (drag?.moved && !pinch && viewportRect && pointersRef.current.size <= 1) {
        startInertia(drag, viewportRect);
      }
    }
    dragRef.current = null;
    pinchRef.current = null;
    if (pointersRef.current.size === 1) {
      const [, remaining] = [...pointersRef.current.entries()][0];
      dragRef.current = {
        x0: remaining.x,
        y0: remaining.y,
        lastX: remaining.x,
        lastY: remaining.y,
        origin: { ...targetView.current },
        rect: viewportRect ?? {
          left: 0,
          top: 0,
          width: 0,
          height: 0,
        },
        moved: false,
        samples: [{ x: remaining.x, y: remaining.y, t: performance.now() }],
        frame: null,
      };
    }
    setDragging(pointersRef.current.size > 0);
  }

  return (
    <div
      ref={containerRef}
      className={`warehouse-map-2d level-${selection.level} ${dragging ? "is-dragging" : ""}`}
      data-level={selection.level}
      aria-label="可缩放二维仓储平面图，支持触摸板双指缩放、拖拽平移和点击下钻"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div className="warehouse-map-hud" aria-live="polite">
        <div className="warehouse-hud-heading">
          <span>WAREHOUSE DIGITAL TWIN</span>
          <strong>{hudTitle}</strong>
        </div>
        <div className="warehouse-hud-metrics">
          {hudMetrics.map((metric) => (
            <span key={metric.label} className={`is-${metric.tone}`}>
              <i />
              <small>{metric.label}</small>
              <b>{metric.value.toLocaleString("zh-CN")}</b>
            </span>
          ))}
        </div>
        <div className="warehouse-hud-meta">
          <span ref={zoomReadoutRef}>100%</span>
          <i />
          <span>{dataStatus}</span>
        </div>
      </div>
      <svg
        ref={svgRef}
        viewBox={ZONES_VIEW_BOX}
        preserveAspectRatio="xMidYMid meet"
        role="group"
        tabIndex={0}
        aria-label={`${layout.zone} 库区二维平面，当前${
          selection.level === "zones"
            ? `${WAREHOUSE_ZONE_COUNT} 个真实库区总览`
            :
          selection.level === "overview"
            ? layout.zone === WAREHOUSE_SPECIAL_ZONE
              ? "特殊库位总览"
              : "库区总览"
            : selection.level === "main"
              ? `主通道 ${selection.mainAisle}`
              : selection.level === "small"
                ? `小通道 ${selection.mainAisle}-${selection.smallAisle}`
                : selection.level === "rack"
                  ? `单货架 ${selection.selectedCode}`
                  : "完整库位图"
        }`}
      >
        <defs>
          <linearGradient id="warehouse-slab-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#16271f" />
            <stop offset=".52" stopColor="#0a1711" />
            <stop offset="1" stopColor="#050c08" />
          </linearGradient>
          <linearGradient id="warehouse-main-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#779ba5" />
            <stop offset=".48" stopColor="#52747e" />
            <stop offset="1" stopColor="#3d5d66" />
          </linearGradient>
          <linearGradient id="warehouse-rack-stocked-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#e7f7ed" />
            <stop offset=".56" stopColor="#d4efdf" />
            <stop offset="1" stopColor="#b9e3ca" />
          </linearGradient>
          <linearGradient id="warehouse-rack-empty-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#e7ebe8" />
            <stop offset="1" stopColor="#cbd1cd" />
          </linearGradient>
          <pattern id="warehouse-hazard-lines" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="12" height="12" fill="rgba(223,201,144,.025)" />
            <path d="M0 0 V12" stroke="rgba(223,201,144,.18)" strokeWidth="3" />
          </pattern>
          <filter id="warehouse-soft-shadow" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="9" stdDeviation="9" floodColor="#000" floodOpacity=".32" />
          </filter>
          <linearGradient id="warehouse-zone-main-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#18362c" />
            <stop offset="1" stopColor="#10251d" />
          </linearGradient>
          <linearGradient id="warehouse-zone-aux-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#192a2d" />
            <stop offset="1" stopColor="#101d1f" />
          </linearGradient>
        </defs>
        {selection.level === "zones" && (
          <g className="warehouse-scene-layer" data-active="true">
            <ZonesScene
              zones={zones}
              activeZone={layout.zone}
              runAction={runAction}
              onOpenZone={onOpenZone}
            />
          </g>
        )}
        {selection.level === "overview" && (
          <g className="warehouse-scene-layer" data-active="true">
            {layout.zone === WAREHOUSE_SPECIAL_ZONE ? (
              <SpecialLocationsScene
                layout={layout}
                runAction={runAction}
                onOpenRack={onOpenRack}
              />
            ) : (
              <OverviewScene layout={layout} runAction={runAction} onOpenMain={onOpenMain} />
            )}
          </g>
        )}
        {selection.level === "main" && (
          <g
            className="warehouse-scene-layer"
            data-active={selection.level === "main"}
            aria-hidden={selection.level !== "main"}
          >
            <MainAisleScene
              layout={layout}
              selection={selection}
              filter={filter}
              recommendedSmallAisles={recommendedSmallAisles}
              runAction={runAction}
              onOpenSmall={onOpenSmall}
            />
          </g>
        )}
        {selection.level === "small" && (
          <g
            className="warehouse-scene-layer"
            data-active={selection.level === "small"}
            aria-hidden={selection.level !== "small"}
          >
            <SmallAisleScene
              layout={layout}
              selection={selection}
              filter={filter}
              products={products}
              productNamesByLocation={productNamesByLocation}
              recommendedLocations={recommendedLocations}
              runAction={runAction}
              onOpenRack={onOpenRack}
            />
          </g>
        )}
        {selection.level === "rack" && selectedRack && (
          <g
            className="warehouse-scene-layer"
            data-active={selection.level === "rack"}
            aria-hidden={selection.level !== "rack"}
          >
            <RackScene
              layout={layout}
              node={selectedRack}
              products={products.get(selectedRack.code) ?? []}
              productNames={productNamesByLocation.get(selectedRack.code) ?? []}
              batches={batchesByLocation.get(selectedRack.code) ?? []}
              recommended={recommendedLocations.has(selectedRack.code)}
            />
          </g>
        )}
        {selection.level === "full" && (
          <g
            className="warehouse-scene-layer"
            data-active={selection.level === "full"}
            aria-hidden={selection.level !== "full"}
          >
            <FullWarehouseScene
              layout={layout}
              selectedCode={selection.selectedCode}
              detailed={detailed}
              runAction={runAction}
              onOpenMain={onOpenMain}
            />
          </g>
        )}
      </svg>
      <div className="map-interaction-hint">
        <span>{selection.level === "zones" ? "点击真实库区进入" : "双指 / 滚轮缩放"}</span>
        <i />
        <span>拖拽平移</span>
        <i />
        <span>
          {selection.level === "zones"
            ? `${WAREHOUSE_ZONE_COUNT} 库区 + ${WAREHOUSE_SPECIAL_LOCATIONS.length} 特殊库位`
            : "点击下钻"}
        </span>
      </div>
      <div className="map-zoom-controls" aria-label="平面图缩放">
        <button type="button" aria-label="放大" onClick={() => zoom(0.78)}><Plus size={15} /></button>
        <button type="button" aria-label="缩小" onClick={() => zoom(1.22)}><Minus size={15} /></button>
        <button type="button" aria-label="恢复视图" onClick={() => animateTo(baseView)}><RotateCcw size={14} /></button>
      </div>
    </div>
  );
}
